import { useCallback, useEffect, useMemo } from "react";

import { DocumentVersion, MediaKind } from "@perceptron/utils/types";

import { MediaFactory } from "@demo/libs/media-factory";
import { useIsDevModeActive } from "@demo/store/dev-mode";
import { useMediaStore } from "@demo/store/media";
import { useModelStore } from "@demo/store/model";
import { Role } from "@demo/types/base";

/** Why the Tools toggle is greyed out, in the order the footer explains it. */
export type ToolsLockReason = "model" | "media" | "thinking" | undefined;

export const useModelConfiguration = () => {
  const {
    enableReasoning: isThinkingEnabled,
    setEnableReasoning,
    enabledTools,
    setEnabledTools,
    enableAudioInVideo,
    setEnableAudioInVideo,
    outputFormat,
    selectedModel,
    setOutputFormat,
    temperature,
    setTemperature,
  } = useModelStore();

  const isDevMode = useIsDevModeActive();
  const activeMedia = useMediaStore((state) => state.targetMedia);
  const mediaSets = useMediaStore((state) => state.mediaSets);

  const canModelThink = useMemo(() => selectedModel.capabilities.reasoning.supported, [selectedModel]);
  // The one Tools toggle turns on every family the model offers: the search tools on Mk1.5.
  const modelTools = selectedModel.capabilities.tools;
  const isToolsEnabled = enabledTools.length > 0;
  const canModelHearAudio = selectedModel.capabilities.audioInVideo;
  const isAudioEnabled = enableAudioInVideo === true;

  const activeMediaKind = useMemo(
    () => (activeMedia ? MediaFactory.detectMediaKind(activeMedia) : null),
    [activeMedia],
  );

  const isThinkingLocked = !canModelThink || !activeMedia;

  // The kinds of media in the draft prompt: every asset in it, not just the active one.
  const draftMediaKinds = useMemo(() => {
    const kinds = new Set<MediaKind>();
    if (activeMediaKind !== null) kinds.add(activeMediaKind);
    for (const set of mediaSets) {
      if (set.role !== Role.USER || set.documentVersion !== DocumentVersion.Draft) continue;
      for (const item of set.media) kinds.add(MediaFactory.detectMediaKind(item));
    }
    return kinds;
  }, [activeMediaKind, mediaSets]);
  // Tools work on image-only prompts; the server drops the search tools for any other run anyway.
  const hasNonImageMedia = [...draftMediaKinds].some((kind) => kind !== MediaKind.Image);
  // The Audio toggle only means something with a video in the prompt, so it is greyed out without one.
  const isAudioLocked = !draftMediaKinds.has(MediaKind.Video);
  // Search runs through the Agent harness, which needs the model thinking. The toggle is always
  // shown; these lock it.
  const toolsLockReason: ToolsLockReason =
    modelTools.length === 0
      ? "model"
      : hasNonImageMedia
        ? "media"
        : modelTools.includes("search") && !isThinkingEnabled
          ? "thinking"
          : undefined;
  const isToolsLocked = toolsLockReason !== undefined || !activeMedia;

  const handleThinkingToggle = useCallback(
    (shouldEnable: boolean) => {
      setEnableReasoning(shouldEnable);
    },
    [setEnableReasoning],
  );

  const handleToolsToggle = useCallback(
    (shouldEnable: boolean) => {
      setEnabledTools(shouldEnable && toolsLockReason === undefined ? [...modelTools] : []);
    },
    [setEnabledTools, toolsLockReason, modelTools],
  );

  const handleAudioToggle = useCallback(
    (shouldEnable: boolean) => {
      setEnableAudioInVideo(shouldEnable);
    },
    [setEnableAudioInVideo],
  );

  useEffect(() => {
    // Missing media is normal on a fresh visit: keep valid restored preferences
    // until an actual upload requires a different format or disables tools.
    const formats = selectedModel.outputFormats;
    const compatibleFormats = formats.filter(
      (format) => !activeMediaKind || format.capabilities.media.includes(activeMediaKind),
    );
    const preferredFormat = activeMediaKind === MediaKind.Video ? "CLIP" : "BOX";
    const nextFormat =
      compatibleFormats.find((format) => format.value === outputFormat.value) ??
      (activeMediaKind && compatibleFormats.find((format) => format.value === preferredFormat)) ??
      compatibleFormats[0];
    if (nextFormat && nextFormat !== outputFormat) {
      setOutputFormat(formats.indexOf(nextFormat));
    }

    if (!canModelThink && isThinkingEnabled) {
      setEnableReasoning(false);
    }
    // Tools that are on follow the model (switching to a model without tools drops them) and turn
    // off when the prompt or thinking no longer allows them.
    if (enabledTools.length > 0) {
      const next = toolsLockReason === undefined ? modelTools : [];
      if (next.join() !== enabledTools.join()) setEnabledTools([...next]);
    }

    if (canModelThink && selectedModel.capabilities.reasoning.alwaysEnabled && !isThinkingEnabled) {
      setEnableReasoning(true);
    }
  }, [
    activeMediaKind,
    selectedModel,
    canModelThink,
    isThinkingEnabled,
    enabledTools,
    modelTools,
    toolsLockReason,
    outputFormat,
    setOutputFormat,
    setEnableReasoning,
    setEnabledTools,
  ]);

  return {
    // Data
    activeMedia,
    currentTemperature: temperature,
    outputFormat,
    isDevMode,

    // State Flags
    isThinkingEnabled,
    isToolsEnabled,
    isAudioEnabled,

    // Capabilities & UI States
    canModelThink,
    isThinkingLocked,
    isToolsLocked,
    toolsLockReason,
    canModelHearAudio,
    isAudioLocked,

    // Actions
    handleThinkingToggle,
    handleToolsToggle,
    handleAudioToggle,
    setTemperature,
  };
};
